import React, { useEffect, useState, useRef } from 'react';
import useForceUpdate from 'use-force-update';
import { isNumber } from '../../Utils/Common';
import PropagateLoader from "react-spinners/PropagateLoader";
import { POST_AEPS_MINI_STATEMENT } from '../../Utils/appConstants';
import { postRequest } from '../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import Select from 'react-select';
import axios from 'axios';
import { Checkbox, Table, Divider, Button } from 'antd';
import BioDevice from './bioDevice';
import xmlJs from 'xml-js';
import { getUser } from "../../Utils/Common";

// ====================== RD SERVICE - PORT ONLY DISCOVERY (scheme locked to DB) ======================
const PORTS = Array.from({ length: 21 }, (_, i) => 11100 + i); // 11100-11120
const RD_CACHE_KEY = 'rd_service_port_v1';
const RD_CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const RD_TIMEOUT = 800;

const getCachedPort = () => {
    try {
        const raw = localStorage.getItem(RD_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.ts > RD_CACHE_TTL) return null;
        return parsed.port;
    } catch {
        return null;
    }
};

const setCachedPort = (port) => {
    localStorage.setItem(RD_CACHE_KEY, JSON.stringify({ port, ts: Date.now() }));
};

const clearRDCache = () => {
    localStorage.removeItem(RD_CACHE_KEY);
};

const replacePort = (url, newPort) => {
    if (!url || !newPort) return url;
    try {
        return url.replace(/:(\d+)(?=\/|$)/, `:${newPort}`);
    } catch {
        return url;
    }
};

/** Extract scheme from DB URL → "http" or "https" */
const getSchemeFromUrl = (url) => {
    if (!url) return 'http';
    const m = String(url).match(/^(https?):\/\//i);
    return m ? m[1].toLowerCase() : 'http';
};

const probePort = async (scheme, port) => {
    try {
        const res = await axios({
            method: 'RDSERVICE',
            url: `${scheme}://127.0.0.1:${port}/`,
            headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
            data: ' ',
            timeout: RD_TIMEOUT,
            validateStatus: () => true,
        });

        if (res.status < 200 || res.status >= 400) return false;
        const xml = typeof res.data === 'string' ? res.data : '';
        return xml.toLowerCase().includes('rdservice');
    } catch {
        return false;
    }
};

/**
 * Discover working port using ONLY the scheme from DB URL
 * Never switches http ↔ https
 *
 * force=false → try cache first; if dead, full scan
 * force=true  → ignore cache, scan all ports 11100-11120
 */
const discoverPort = async (force = false, preferredScheme = 'http') => {
    const scheme = preferredScheme === 'https' ? 'https' : 'http';

    // 1. Try cache with same scheme only
    if (!force) {
        const cached = getCachedPort();
        if (cached) {
            const alive = await probePort(scheme, cached);
            if (alive) return cached;
            clearRDCache();
        }
    } else {
        clearRDCache();
    }

    // 2. Full scan — same scheme only (no http↔https flip)
    for (const port of PORTS) {
        const alive = await probePort(scheme, port);
        if (alive) {
            setCachedPort(port);
            return port;
        }
    }

    return null;
};
// ====================== END RD SERVICE ======================

function MiniStatement(props) {
    const history = useHistory();
    const forceUpdate = useForceUpdate();
    const [isLoading, setLoading] = useState(false);
    const [getDataField, setDataField] = useState(props.existData ? props.existData : []);
    const [getDataError, setDataError] = useState({});
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [isConsent, setConsent] = useState("N");
    const [biometricData, setBiometricData] = useState(null);
    const [getBankList, setBankList] = useState([]);
    const [isSubmit, setSubmit] = useState(false);
    const [dataRecord, setDataRecord] = useState([]);
    const [fingerprintXmlData, setFingerprintXmlData] = useState(null);

    const bankSelect = useRef("");
    const aadharInput = useRef(null);
    const mobileInput = useRef(null);

    const [isDeviceConnected, setIsDeviceConnected] = useState(false);

    // RD Service states
    const [activePort, setActivePort] = useState(null);
    const [isDiscovering, setIsDiscovering] = useState(false);

    // Capture lock — prevents frequent info/capture calls
    const isCapturingRef = useRef(false);

    // Keep original DB URLs (never overwrite permanently)
    const originalConfigRef = useRef({ infoUrl: null, captureUrl: null, xmlData: null });

    // ---------- Get final URLs (original DB URL + discovered port) ----------
    const getInfoUrl = (port = activePort) => {
        const base = originalConfigRef.current.infoUrl || props.deviceConfig?.infoUrl;
        if (!base) return null;
        return port ? replacePort(base, port) : base;
    };

    const getCaptureUrl = (port = activePort) => {
        const base = originalConfigRef.current.captureUrl || props.deviceConfig?.captureUrl;
        if (!base) return null;
        return port ? replacePort(base, port) : base;
    };

    // ---------- Discover port ----------
    const discoverDevice = async (force = false) => {
        setIsDiscovering(true);
        try {
            // Save originals once
            if (props.deviceConfig?.infoUrl && !originalConfigRef.current.infoUrl) {
                originalConfigRef.current = {
                    infoUrl: props.deviceConfig.infoUrl,
                    captureUrl: props.deviceConfig.captureUrl,
                    xmlData: props.deviceConfig.xmlData,
                };
            }

            // Scheme locked to DB URL — never flips
            const baseInfoUrl = originalConfigRef.current.infoUrl || props.deviceConfig?.infoUrl;
            const scheme = getSchemeFromUrl(baseInfoUrl);

            const port = await discoverPort(force, scheme);
            if (port) {
                setActivePort(port);

                const finalInfoUrl = getInfoUrl(port);
                const connected = await checkDeviceConnected(finalInfoUrl);
                setIsDeviceConnected(connected);
                return port;
            } else {
                setActivePort(null);
                setIsDeviceConnected(false);
                return null;
            }
        } catch (err) {
            console.error('Discovery error:', err);
            setActivePort(null);
            setIsDeviceConnected(false);
            return null;
        } finally {
            setIsDiscovering(false);
        }
    };

    // ---------- Check device connected ----------
    const checkDeviceConnected = async (infoUrl) => {
        if (!infoUrl) return false;
        try {
            const res = await axios({
                method: 'DEVICEINFO',
                url: infoUrl,
                headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
                data: ' ',
                timeout: 3000,
                validateStatus: () => true,
            });

            if (res.status < 200 || res.status >= 400) return false;

            const jsonData = xmlJs.xml2json(res.data, { compact: true });
            const parsed = JSON.parse(jsonData);

            if (!parsed?.DeviceInfo) return false;

            const params = parsed?.DeviceInfo?.additional_info?.Param;

            let srnoValue = null;
            if (Array.isArray(params)) {
                const srnoParam = params.find((p) => p._attributes?.name === 'srno');
                srnoValue = srnoParam?._attributes?.value;
            } else if (params?._attributes?.name === 'srno') {
                srnoValue = params._attributes?.value;
            }

            return !!srnoValue && srnoValue.trim() !== '';
        } catch {
            return false;
        }
    };

    // ---------- Device detection ----------
    const detectFingerprintDevice = async () => {
        const infoUrl = getInfoUrl();
        if (!infoUrl) return false;
        return await checkDeviceConnected(infoUrl);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
        },
        {
            title: 'Type',
            dataIndex: 'txnType',
        },
        {
            title: 'Narration',
            dataIndex: 'narration',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
        },
    ];

    // ---------- Form handlers ----------
    const onChangeValueSelect = (event) => {
        const { value } = event;
        props.existInputData(event, value);
        setDataField({ ...getDataField, "bankiin": value });
    };

    const clearFormField = () => {
        props.clearExistInputData();
        setConsent('N');
        setDataError({});
        setDataField([]);
        setFingerprintXmlData(null);
        setBiometricData(null);
        isCapturingRef.current = false;
    };

    const onChangeValue = (event) => {
        props.existInputData(event);
        const { name, value } = event.target;
        setDataField({ ...getDataField, [name]: value });
    };

    const FormValidate = () => {
        let formValid = true;
        let formObject = {};
        setDataError({});
        if (getDataField && getDataField.length === 0) {
            formValid = false;
        }
        let array = ["bankiin", "aadhaar", "mobile"];
        array.forEach(element => {
            if (getDataField[element] === "" || typeof getDataField[element] === "undefined") {
                formObject[element] = true;
                formValid = false;
            }
        });

        setDataError(formObject);
        return formValid;
    };

    // ---------- Fingerprint Capture ----------
    const scanFingerPrintData = async () => {
        // Hard lock — prevents frequent info/capture calls
        if (isCapturingRef.current) {
            console.log("Capture already running — skipped");
            return;
        }
        isCapturingRef.current = true;

        try {
            // 1. Ensure port
            let port = activePort;
            if (!port) {
                port = await discoverDevice(true);
                if (!port) {
                    toast.error("No RD Service found. Please install/start RD Service for your biometric device.");
                    setConsent('N');
                    return;
                }
            }

            // 2. DEVICEINFO — only once
            let status = await detectFingerprintDevice();
            setIsDeviceConnected(status);

            // If current port fails → force rediscover (same scheme)
            if (!status) {
                console.log(`Port ${port} failed / no device. Scanning all ports...`);
                clearRDCache();
                setActivePort(null);

                port = await discoverDevice(true);
                if (!port) {
                    toast.error("No RD Service found on any port. Please check device.");
                    setConsent('N');
                    setIsDeviceConnected(false);
                    return;
                }

                status = await detectFingerprintDevice();
                setIsDeviceConnected(status);

                if (!status) {
                    toast.error("Device not connected. Please check USB connection and try again.");
                    setConsent('N');
                    setIsDeviceConnected(false);
                    return;
                }
            }

            // 3. CAPTURE — only once
            const captureUrl = getCaptureUrl(port);
            const xmlData =
                originalConfigRef.current.xmlData ||
                props.deviceConfig?.xmlData ||
                `<PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" iType="0" pCount="0" format="0" pidVer="2.0" timeout="10000" posh="UNKNOWN" env="P" wadh=""/></PidOptions>`;

            const response = await axios({
                method: 'CAPTURE',
                url: captureUrl,
                headers: { "Content-Type": "text/xml; charset=UTF-8" },
                data: xmlData,
                timeout: 30000,
            });

            const jsonData = xmlJs.xml2json(response.data, { compact: true, spaces: 4 });
            const XMLjson = JSON.parse(jsonData);

            if (!jsonData || jsonData.length < 1500) {
                toast.error("Invalid capture response. Please try again.");
                setFingerprintXmlData(null);
                return;
            }

            if (XMLjson?.PidData?.Resp?._attributes?.errCode === "0") {
                setFingerprintXmlData(response.data);
            } else {
                const errCode = XMLjson?.PidData?.Resp?._attributes?.errCode;
                const errInfo = XMLjson?.PidData?.Resp?._attributes?.errInfo;
                toast.error(`Capture failed (${errCode || 'Unknown'}): ${errInfo || 'Please try again'}`);
                setFingerprintXmlData(null);

                // Clear cache so next attempt rediscovers
                clearRDCache();
                setActivePort(null);
            }
        } catch (error) {
            console.error("Capture error:", error);
            toast.error("Capture failed. Will rediscover port on next attempt.");
            clearRDCache();
            setActivePort(null);
            setIsDeviceConnected(false);
        } finally {
            isCapturingRef.current = false;
        }
    };

    const scanFinger = async () => {
        if (FormValidate()) {
            if (isConsent !== 'Y') {
                toast.error("Please accept consent declaration");
                return;
            }

            if (!activePort) {
                const port = await discoverDevice(true);
                if (!port) {
                    toast.error("No RD Service found. Please install/start RD Service.");
                    return;
                }
            }

            scanFingerPrintData();
        }
    };

    const handleSubmit = () => {
        if (FormValidate()) {
            setLoading(true);
            const payload = {
                bankiin: getDataField.bankiin,
                mobile: getDataField.mobile,
                biometricData: biometricData,
                aadhaar: getDataField.aadhaar,
                latitude: latitude,
                longitude: longitude,
                isXml: true,
                consent: isConsent,
            };

            postRequest(POST_AEPS_MINI_STATEMENT, payload, history).then((response) => {
                if (response.success === true) {
                    setSubmit(true);
                    setDataRecord(response.data);
                } else {
                    toast.error(response.message);
                    if (response?.rekyc === true || response?.rekyc === "true" || response?.rekyc === 1) {
                        history.push('/partner-ekyc');
                        return;
                    }
                }
                setLoading(false);
                setSubmit(true);
            });
        }
    };

    // ---------- Consent checkbox ----------
    const onChangeChackBox = async (e) => {
        const newChecked = e.target.checked;
        if (newChecked) {
            if (!FormValidate()) {
                toast.error("Please fill all required fields and ensure validations are met");
                return;
            }

            let port = activePort;
            if (!port) {
                port = await discoverDevice(false);
            }

            let status = await detectFingerprintDevice();

            // If failed → force rescan other ports (same scheme)
            if (!status) {
                clearRDCache();
                setActivePort(null);
                port = await discoverDevice(true);
                status = await detectFingerprintDevice();
            }

            setIsDeviceConnected(status);
            if (!status) {
                toast.error("Device not connected, check connection and try again");
                return;
            }
        }
        setConsent(newChecked ? 'Y' : 'N');
    };

    const clearMiniStatement = () => {
        props.clearExistInputData();
        setSubmit(false);
        setDataError({});
        setDataField({});
        setDataRecord([]);
        clearFormField();
    };

    // ---------- useEffect hooks ----------
    useEffect(() => {
        // Discover port only once on mount (scheme taken from props.deviceConfig)
        if (props.deviceConfig?.infoUrl) {
            discoverDevice(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When deviceConfig arrives later from parent → save originals + discover
    useEffect(() => {
        if (props.deviceConfig?.infoUrl && !originalConfigRef.current.infoUrl) {
            originalConfigRef.current = {
                infoUrl: props.deviceConfig.infoUrl,
                captureUrl: props.deviceConfig.captureUrl,
                xmlData: props.deviceConfig.xmlData,
            };
            discoverDevice(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.deviceConfig?.infoUrl]);

    useEffect(() => {
        if (fingerprintXmlData) {
            setBiometricData(fingerprintXmlData);
        }
    }, [fingerprintXmlData]);

    useEffect(() => {
        if (biometricData) {
            handleSubmit();
        }
        if (props.getBankList && props.getBankList.length > 0) {
            setBankList(props.getBankList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [biometricData, props.getBankList]);

    useEffect(() => {
        const userData = getUser();
        const shopsLat = userData?.data?.shopsLat;
        const shopsLong = userData?.data?.shopsLong;

        if (
            shopsLat !== undefined &&
            shopsLat !== null &&
            Number(shopsLat) !== 0
        ) {
            setLatitude(Number(shopsLat));
            setLongitude(Number(shopsLong));
        } else {
            navigator.geolocation.getCurrentPosition((data) => {
                setLatitude(data.coords.latitude);
                setLongitude(data.coords.longitude);
            });
        }
    }, []);

    useEffect(() => {
        if (props.existData) {
            forceUpdate();
        }
    }, [props.existData]);

    useEffect(() => {
        return () => {
            isCapturingRef.current = false;
        };
    }, []);

    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: "250px 0" }}>
                <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
            </div>
        );
    }

    return (
        <div>
            <div className="row">
                <div className="col-md-12">
                    {!isSubmit ? (
                        <div className='card rounded-0 card-body'>
                            <div className='row'>
                                <div className='col-sm-6 col-md-4 col-lg-4'>
                                    <div className="form-group">
                                        <div className="input-group">
                                            <label>Bank Name</label>
                                            <Select
                                                showSearch
                                                status={`${getDataError && getDataError.bank ? "error" : ""}`}
                                                options={getBankList}
                                                className={`normal-light w-100`}
                                                classNamePrefix="select"
                                                ref={bankSelect}
                                                name='bankiin'
                                                onChange={(e) => onChangeValueSelect(e)}
                                                placeholder="-Select Bank-"
                                            />
                                            {getDataError && getDataError.bankiin && <span className="error text-danger">The field is required</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className='col-sm-6 col-md-4 col-lg-4'>
                                    <div className="form-group">
                                        <label>Aadhaar Number</label>
                                        <input
                                            type="text"
                                            className={`form-control normal-light ${getDataError && getDataError.aadhaar ? "is-invalid" : ""}`}
                                            name="aadhaar"
                                            maxLength={12}
                                            value={getDataField && getDataField.aadhaar}
                                            onKeyDown={isNumber}
                                            onChange={(e) => onChangeValue(e)}
                                            ref={aadharInput}
                                        />
                                        {getDataError && getDataError.aadhaar && <span className="error text-danger">The field is required</span>}
                                    </div>
                                </div>
                                <div className='col-sm-6 col-md-4 col-lg-4'>
                                    <div className="form-group">
                                        <label>Mobile Number</label>
                                        <input
                                            type="text"
                                            className={`form-control normal-light ${getDataError && getDataError.mobile ? "is-invalid" : ""}`}
                                            name="mobile"
                                            value={getDataField && getDataField.mobile}
                                            onKeyDown={isNumber}
                                            onChange={(e) => onChangeValue(e)}
                                            maxLength={10}
                                            ref={mobileInput}
                                        />
                                        {getDataError && getDataError.mobile && <span className="error text-danger">The field is required</span>}
                                    </div>
                                </div>
                                <div className='col-sm-12 col-md-12 col-lg-12'>
                                    <div className='form-group'>
                                        <Checkbox
                                            className='d-flex'
                                            checked={isConsent === "Y"}
                                            onChange={onChangeChackBox}
                                        >
                                            I hereby give my voluntary consent to SR PAY Private Limited, use my Aadhaar/ VID number & biometric to authenticate from UIDAI for the purpose of AEPS activation.
                                        </Checkbox>
                                    </div>
                                </div>
                                <div className='col-sm-12 col-md-12 col-lg-12'>
                                    <div className='col-sm-4 col-md-4 col-lg-4'>
                                        <BioDevice />
                                    </div>
                                </div>

                                <div className='col-sm-6 col-md-4 col-lg-4'>
                                    <label></label>

                                    {/* Buttons Row */}
                                    <div className='col-md-12 d-flex align-items-center'>
                                        <div className="form-group mr-3">
                                            <button
                                                type="button"
                                                className="btn btn-default rounded-pill pl-4 pr-4"
                                                onClick={clearFormField}
                                            >
                                                Cancel
                                            </button>
                                        </div>

                                        <div className="form-group">
                                            <button
                                                type="button"
                                                className="btn btn-primary rounded-pill pl-4 pr-4"
                                                onClick={scanFinger}
                                                disabled={isConsent === 'N' || !isDeviceConnected || isDiscovering}
                                            >
                                                <i className="fas fa-fingerprint"></i>{" "}
                                                {isDiscovering ? "Searching..." : "Scan Finger"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Device Status */}
                                    <div className="col-md-12 mt-2">
                                        {isDiscovering ? (
                                            <span className="text-primary fw-bold">🔍 Searching for biometric device...</span>
                                        ) : isDeviceConnected ? (
                                            <span className="text-success fw-bold">
                                                🟢 Fingerprint Device Connected
                                               
                                            </span>
                                        ) : (
                                            <span className="text-danger fw-bold">
                                                🔴 Fingerprint Device Not Connected
                                            </span>
                                        )}
                                    </div>

                                    {/* Optional hint */}
                                    {!isDeviceConnected && !isDiscovering && (
                                        <div className="col-md-12 text-muted small mt-1">
                                            Please connect fingerprint device to enable scanning
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div>
                                <div>Bank Name: <span className='text-primary fw-bold'>{dataRecord.bankName}</span></div>
                                <div className='text-right'>Bank Balance: <span className='text-primary fw-bold'>{dataRecord.bankAccountBalance} INR</span></div>
                            </div>
                            <Divider><span className='text-danger'>Mini Statement</span></Divider>
                            <Table columns={columns} dataSource={dataRecord.miniStatement} size="small" />
                            <div className='text-right'>
                                <Button className='bg-primary mr-3' onClick={() => props.subChangeTab('1')}>Cash Withdrawal</Button>
                                <Button className='bg-primary mr-3' onClick={() => clearMiniStatement()}>New Transaction</Button>
                                <Button className='bg-info' onClick={() => scanFinger()}>Retry</Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MiniStatement;